/**
 * Survey Says SFX module — mp3-backed buffer cache, mirrors the WhatIs
 * pattern. Logical cue methods (chime/ding/buzz/tick) load mp3s from
 * src/assets/sfx and route through audioBus.sfxGain().
 *
 * Cues:
 *   chime — question opens / game over reveal
 *   ding  — surveyed answer reveals
 *   buzz  — round-end with no scorers
 *   tick  — countdown last 5 seconds
 */

import chimeUrl from '../assets/sfx/chime.mp3?url';
import dingUrl from '../assets/sfx/ding.mp3?url';
import buzzUrl from '../assets/sfx/buzz.mp3?url';
import tickUrl from '../assets/sfx/tick.mp3?url';

import { audioBus } from './bus.js';

export type SfxName = 'chime' | 'ding' | 'buzz' | 'tick';

const SFX_URLS: Record<SfxName, string> = {
  chime: chimeUrl,
  ding: dingUrl,
  buzz: buzzUrl,
  tick: tickUrl,
};

const buffers = new Map<SfxName, AudioBuffer | null>();
const inflight = new Map<SfxName, Promise<AudioBuffer | null>>();

async function loadBuffer(name: SfxName): Promise<AudioBuffer | null> {
  if (buffers.has(name)) return buffers.get(name) ?? null;
  const existing = inflight.get(name);
  if (existing) return existing;
  const ctx = audioBus.context();
  if (!ctx) return null;
  const url = SFX_URLS[name];
  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      buffers.set(name, buf);
      return buf;
    } catch (err) {
      console.warn(`[ss-sfx] failed to load ${name} (${url}):`, err);
      buffers.set(name, null);
      return null;
    } finally {
      inflight.delete(name);
    }
  })();
  inflight.set(name, p);
  return p;
}

function playBuffer(ctx: AudioContext, buf: AudioBuffer, name: SfxName): void {
  try {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(audioBus.sfxGain());
    src.start(0);
  } catch (err) {
    console.warn(`[ss-sfx] play ${name} failed:`, err);
  }
}

function play(name: SfxName): void {
  const ctx = audioBus.context();
  if (!ctx) return;
  const buf = buffers.get(name);
  if (!buf) {
    void loadBuffer(name).then((b) => {
      if (b && audioBus.isUnlocked()) playBuffer(ctx, b, name);
    });
    return;
  }
  if (!audioBus.isUnlocked()) {
    void audioBus.unlock();
  }
  playBuffer(ctx, buf, name);
}

export const sfx = {
  async preload(): Promise<void> {
    const names = Object.keys(SFX_URLS) as SfxName[];
    await Promise.all(names.map((n) => loadBuffer(n)));
  },
  play,
  chime(): void { play('chime'); },
  ding(): void { play('ding'); },
  buzz(): void { play('buzz'); },
  tick(): void { play('tick'); },
};
